import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import ListItem from 'components/Luxkit/List/ListItem'
import ListItemLoadingSkeleton from 'components/Luxkit/List/ListItemLoadingSkeleton'
import { rem } from 'polished'
import React, { useCallback, useContext } from 'react'
import Caption from 'components/Luxkit/Typography/Caption'
import LineShipIcon from 'components/Luxkit/Icons/line/LineShipIcon'
import CruiseLineListItem from './CruiseLineListItem'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { GlobalSearchDispatchContext, GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import { without } from 'lib/arrayUtils'
import useCruiseSearchFacets from 'hooks/Cruise/useCruiseSearchFacets'
import cx from 'clsx'

const CruiseLineSearchWrapper = styled(VerticalSpacer)`
  padding: ${rem(16)} 0;
`

const CruiseLineListWrapper = styled.div`
  display: flex;
  padding: ${rem(8)} 0;
  align-items: center;
  flex-wrap: wrap;
  overflow-y: auto;
  justify-content: center;
  display: grid;
  gap: ${rem(12)};
  grid-template-columns: repeat(3, 1fr);

  ${mediaQueryUp.tablet} {
    grid-template-columns: repeat(7, 1fr);
    justify-content: flex-start;
    gap: ${rem(12)};
    max-height: ${rem(340)};
  }

  &.drawer {
    grid-template-columns: repeat(3, 1fr);
    max-height: unset;
  }
`

const HeadingText = styled(Caption)`
  padding: 0 ${rem(8)};
`

const AllCruiseLinesButtonGrid = styled.div`
  display: grid;
  grid-template-columns: auto;
  gap: ${rem(12)};

  ${mediaQueryUp.tablet} {
    grid-template-columns: repeat(7, 1fr);
  }

  &.drawer {
    grid-template-columns: auto;
  }
`

const AllCruiseLinesButton = styled(ListItem)`
  width: 100%;
  box-shadow: ${(props) => props.theme.shadow.flat.small};

  ${mediaQueryUp.tablet} {
    width: unset;
    grid-column: span 2;
  }

  &.is-selected {
    box-shadow: inset 0px 0px 0px 2px ${({ theme }) => theme.palette.neutral.one};
  }
`

interface Props {
  onSelect?: (cruiseLines: string[] | []) => void;
  drawerMode?: boolean;
}

function CruiseLinesSerchList(props: Props) {
  const { drawerMode, onSelect } = props

  const { cruiseLines: searchCruiseLines } = useContext(GlobalSearchStateContext)
  const searchDispatch = useContext(GlobalSearchDispatchContext)
  const [cruiseLines, fetching] = useCruiseSearchFacets({ facetTypes: ['cruise_lines'] })

  const handleSelectVendor = useCallback((cruiseLine: string) => {
    let nextCruiseLines: string[] = []

    const alreadyExists = searchCruiseLines.includes(cruiseLine)
    if (alreadyExists) {
      nextCruiseLines = without(searchCruiseLines, cruiseLine)
    } else {
      nextCruiseLines = [...searchCruiseLines, cruiseLine]
    }

    searchDispatch({ type: GlobalSearchStateActions.SET_CRUISE_LINES, cruiseLines: nextCruiseLines })
    onSelect?.(nextCruiseLines)
  }, [searchCruiseLines, searchDispatch, onSelect])

  const onSelectAllVendors = useCallback(() => {
    searchDispatch({ type: GlobalSearchStateActions.SET_CRUISE_LINES, cruiseLines: [] })
    onSelect && onSelect([])
  }, [searchDispatch, onSelect])

  return <CruiseLineSearchWrapper gap={16}>
    <AllCruiseLinesButtonGrid className={cx({ drawer: drawerMode })}>
      <AllCruiseLinesButton
        className={cx({ 'is-selected': !searchCruiseLines.length })}
        title="All cruise lines"
        subtitle="Select all available cruise lines"
        startIcon={<LineShipIcon />}
        onClick={onSelectAllVendors}
      />
    </AllCruiseLinesButtonGrid>

    <HeadingText variant="large" weight="bold">Trending cruise lines</HeadingText>

    {fetching && <>
      <ListItemLoadingSkeleton subtitle />
      <ListItemLoadingSkeleton subtitle />
      <ListItemLoadingSkeleton subtitle />
    </>}
    {!fetching && <CruiseLineListWrapper className={cx({ drawer: drawerMode })}>
      {cruiseLines.map(cruiseLine => <CruiseLineListItem
        key={cruiseLine.imageId}
        cruiseLine={cruiseLine}
        isSelected={searchCruiseLines?.includes(cruiseLine.value ?? '')}
        onClick={handleSelectVendor}
      />)}
    </CruiseLineListWrapper>}
  </CruiseLineSearchWrapper>
}

export default CruiseLinesSerchList
